import { Button, Banner, useI18nContext } from "@procore/core-react";
import React, { useEffect, useState } from "react";
import { Excavator, Info } from "@procore/core-icons";
import type { AssignmentFormattedTypes, EquipmentAssignmentsBannerProps } from "./types";
import { EquipmentAssignmentStore } from "@/stores/equipment-assignments-store.core";
import { getEquipmentAssignmentSchedulesFormatted } from "./helpers";
import { ASSIGNMENT_CONTENT_LIMIT } from "./constants";

export const EquipmentAssignmentBanner = (props: EquipmentAssignmentsBannerProps) => {
   const I18n = useI18nContext();
   const { equipmentResourceId, equipmentName, equipmentIdentificationNumber, showBanner } = props;
   const [assignmentsData, setAssignmentsData] = useState<AssignmentFormattedTypes[]>([]);
   const [isExpanded, setIsExpanded] = useState(false);
   const [bannerVisibility, setBannerVisibility] = useState(false);

   useEffect(() => {
      setBannerVisibility(showBanner);
   }, [equipmentResourceId, showBanner]);

   useEffect(() => {
      const fetchData = async () => {
         if (!equipmentResourceId) return;
         const equipmentAssignmentSchedules =
            await EquipmentAssignmentStore.getEquipmentAssignmentSchedules(equipmentResourceId)
               .payload;
         const formattedData = getEquipmentAssignmentSchedulesFormatted(
            equipmentAssignmentSchedules?.data,
         );
         if (formattedData) {
            setAssignmentsData(formattedData);
         }
      };
      fetchData();
   }, [equipmentResourceId]);

   const toggleBannerVisibility = () => {
      setBannerVisibility(false);
   };

   const handleButtonClick = () => {
      setIsExpanded((prev) => !prev);
   };

   return (
      bannerVisibility &&
      assignmentsData.length > 0 && (
         <Banner
            {...props}
            key={equipmentResourceId}
            style={{ marginBottom: "10px" }}
            data-testid={`equipment-assignments-banner-${equipmentResourceId}`}
         >
            <Banner.Icon icon={<Info size="md" />} />
            <Banner.Content>
               <Banner.Title>
                  {I18n.t("views.company.workforce_planning.assignment.schedule_for_text")}{" "}
                  <Excavator size="sm" style={{ verticalAlign: "top" }} />{" "}
                  {equipmentIdentificationNumber} ({equipmentName})
               </Banner.Title>
               <Banner.Body>
                  {assignmentsData.slice(0, ASSIGNMENT_CONTENT_LIMIT).map((assignment) => (
                     <li key={assignment.assignmentId}>{assignment.formattedAssignmentDetails}</li>
                  ))}
                  {isExpanded &&
                     assignmentsData
                        .slice(ASSIGNMENT_CONTENT_LIMIT)
                        .map((assignment) => (
                           <li key={assignment.assignmentId}>
                              {assignment.formattedAssignmentDetails}
                           </li>
                        ))}
               </Banner.Body>
            </Banner.Content>
            {assignmentsData.length > ASSIGNMENT_CONTENT_LIMIT && (
               <Banner.Action>
                  <Button onClick={handleButtonClick}>
                     {isExpanded
                        ? I18n.t("views.company.workforce_planning.show_less")
                        : I18n.t("views.company.workforce_planning.show_more")}
                  </Button>
               </Banner.Action>
            )}
            <Banner.Dismiss
               onClick={toggleBannerVisibility}
               data-testid={`equipment-assignments-banner-dismiss-${equipmentResourceId}`}
            />
         </Banner>
      )
   );
};
